<script setup lang="ts">
import { RouterLink } from "vue-router";
import type { BoxLayout, DisplayMode } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import {
  buildUrlPrefix,
  getProductName,
  getProductRoute,
  getProductFromPrice,
  getBiggestThumbnailUrl,
} from "@shopware/helpers";
import { toRefs, computed, ref } from "vue";
import { defu } from "defu";
import SwListingProductPrice from "././../product/SwListingProductPrice.vue";
import {
  useAddToCart,
  useNotifications,
  useProductWishlist,
  useUrlResolver,
} from "#imports";
import { useElementSize } from "@vueuse/core";
import type { Schemas } from "#shopware";
import { ApiClientError } from "@shopware/api-client";
const { t } = useI18n();
const localePath = useLocalePath();

const { formatLink } = useInternationalization(localePath);

const { pushSuccess, pushError, pushWarning } = useNotifications();

const props = withDefaults(
  defineProps<{
    product: Schemas["Product"];
    layoutType?: BoxLayout;
    isProductListing?: boolean;
    displayMode?: DisplayMode;
  }>(),
  {
    layoutType: "standard",
    displayMode: "standard",
    isProductListing: false,
  }
);

type Translations = {
  product: {
    addedToWishlist: string;
    removedFromTheWishlist: string;
    reason: string;
    cannotAddToWishlist: string;
    addedToCart: string;
    addToCart: string;
    details: string;
    badges: {
      topseller: string;
    };
  };
};

let translations: Translations = {
  product: {
    addedToWishlist: "has been added to wishlist.",
    removedFromTheWishlist: "has been removed from wishlist.",
    reason: "Reason",
    cannotAddToWishlist: "cannot be added to wishlist.",
    addedToCart: "has been added to cart.",
    addToCart: "Add to cart",
    details: "Details",
    badges: {
      topseller: "Tip",
    },
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { product } = toRefs(props);

const { addToCart, isInCart, count } = useAddToCart(
  product.value?.childCount > 0 ? product.value?.children[0] : product
);

const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(
  product.value.id
);
const isLoading = ref(false);

const toggleWishlistProduct = async () => {
  if (!isInWishlist.value) {
    try {
      await addToWishlist();
      return pushSuccess(
        `<b>${props.product?.translated?.name}</b>` +
          t("product.messages.addedToWishlist")
      );
    } catch (error) {
      if (error instanceof ApiClientError) {
        const reason = error.details.errors?.[0]?.detail
          ? `${translations.product.reason}: ${error.details.errors?.[0]?.detail}`
          : "";
        return pushError(
          `<b>${props.product?.translated?.name}</b>` +
            t("product.messages.annotAddToWishlist") +
            `\n${reason}`
        );
      }
    }
  }
  pushWarning(
    `<b>${props.product?.translated?.name}</b> ` +
      t("product.messages.removedFromWishlist")
  );
  removeFromWishlist();
};

const isAddToCartProcessing = ref(false);
const addToCartProxy = async () => {
  isAddToCartProcessing.value = true;
  await addToCart();
  pushSuccess(
    `${props.product?.translated.name} ${translations.product.addedToCart}`
  );
  isAddToCartProcessing.value = false;
};

const { getUrlPrefix } = useUrlResolver();

const imageElement = ref(null);
const { height } = useElementSize(imageElement);

const DEFAULT_THUMBNAIL_SIZE = 10;
function roundUp(num: number) {
  return num ? Math.ceil(num / 100) * 100 : DEFAULT_THUMBNAIL_SIZE;
}

const srcPath = computed(() => {
  return `${getBiggestThumbnailUrl(
    product.value?.cover?.media
  )}?&height=${roundUp(height.value)}&fit=cover`;
});

const isSale = computed(() => {
  return (
    product.value &&
    product.value.calculatedPrice &&
    product.value.calculatedPrice.listPrice &&
    product.value.calculatedPrice.listPrice.discount < 0
  );
});

const isBestPrice = computed(() => {
  return (
    product.value.tagIds &&
    product.value.tagIds.includes("019474d44f8976dcb4d544a70df761da")
  );
});

const isHit = computed(() => {
  return (
    product.value.tagIds &&
    product.value.tagIds.includes("01929aabd1c7794394a95b6e0c5891fe")
  );
});

const isLimitedEdition = computed(() => {
  return (
    product.value.tagIds &&
    product.value.tagIds.includes("01929aac67377f62a7c5837237ba4ba7")
  );
});

const isWoman = computed(() => {
  return (
    product.value.tagIds &&
    product.value.tagIds.includes("019f6f26b51973ada2fae91e8d0a4db6")
  );
});
</script>

<template>
  <div
    class="cards__item pe-[0px] ps-[0px] pb-7 md:pb-10 w-full nach"
    data-testid="product-box"
  >
    <div class="inline-block h-full w-full product_item_wrap">
      <div class="w-full mb-3 relative">
        <figure
          class="d-block w-full h-[300x] sm:h-[270px] md:h-[400px] lg:h-[475px] m-0 relative"
        >
          <!-- <a href="#" class="absolute start-0 top-0 w-full h-full"></a> -->
          <RouterLink
            :to="formatLink(getProductRoute(product))"
            class="overflow-hidden"
          >
            <NuxtImg
              ref="imageElement"
              loading="lazy"
              format="webp"
              :src="srcPath"
              :alt="getProductName({ product }) || ''"
              class="img-cover mCS_img_loaded"
              width="308"
              data-testid="product-box-img"
            />
          </RouterLink>

          <span
            :disabled="isLoading"
            @click="toggleWishlistProduct"
            class="fav-icon top-[10px] end-[10px] absolute text-black03 text-xl"
          >
            <i v-if="isInWishlist" class="fa-solid fa-heart"></i>
            <i v-else class="fa-regular fa-heart"></i>
          </span>
          <div class="absolute bottom-0 lg:top-3 left-0 lg:left-2">
            <div
              v-if="isHit"
              class="font-bold bg-pink text-white text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.hit") }}
            </div>
            <div
              v-else-if="isWoman"
              class="font-bold bg-womanPink text-black text-center rounded-sm text-sm px-5"
            >
              {{ $t("product.badges.woman") }}
            </div>
            <div
              v-else-if="isBestPrice"
              class="font-bold bg-black text-white text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.bestprice") }}
            </div>
            <div
              v-else-if="isSale"
              class="font-bold bg-red-600 text-white text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.sale") }}
            </div>
            <div
              v-else-if="product.isNew"
              class="font-bold bg-white text-black text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.new") }}
            </div>
            <div
              v-else-if="isLimitedEdition"
              class="font-bold bg-black03 text-white text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.limited") }}
            </div>
          </div>
        </figure>
        <!-- <button
          type="button"
          class="absolute z-10 right-2 bottom-2 md:flex btn-produ-slide items-center justify-center btn_secondary text-nowrap p-0 h-[50px] w-[50px] text-lg"
          :class="{
            'opacity-50 cursor-not-allowed': !product.available,
          }"
          data-testid="add-to-cart-button"
          :disabled="!product.available"
          @click="addToCartProxy"
        >
          <i v-if="!isAddToCartProcessing" class="fa-solid fa-cart-plus"></i>
          <span v-else>
            <i class="fa-solid fa-spinner animate-spin"></i>
          </span>
        </button>
        <RouterLink
          :to="formatLink(getProductRoute(product))"
          class="absolute start-0 top-0 w-full h-full flex items-center justify-center overlay-hover uppercase text-white hidden md:flex"
          style="background-color: rgba(0, 0, 0, 0.3)"
        >
          {{ $t("button.buynow.listing") }}
          <i class="fa-solid fa-angle-right"></i>
        </RouterLink> -->
      </div>
      <div class="pt-1">
        <p class="text-sm md:text-lg pe-[15px] ps-0 md:ps-[15px]">
          <RouterLink
            :to="formatLink(getProductRoute(product))"
            class="hover:text-pink truncate"
          >
            {{ getProductName({ product }) }}
          </RouterLink>
        </p>
        <p class="uppercase flex bg-white z-10 pe-[15px] ps-0 md:ps-[15px]">
          <SwListingProductPrice
            :product="product"
            class="flex gap-2 flex-row-reverse"
            data-testid="product-box-product-price"
          />
        </p>
      </div>
    </div>
  </div>
</template>
